import Left from "@repo/icons/default/Left";
import Settings from "@repo/icons/default/Settings";
import { createFileRoute, useNavigate, useRouteContext } from "@tanstack/react-router";
import { graphql } from "@repo/graphql-types/gql";
import { CommunityCurrentUserProfileQueryDocument } from "@repo/graphql-types/graphql";
import { z } from "zod";
import { useState, type JSX } from "react";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { CommunityList } from "@/components/community/list/community-list";
import { CommunityFilterDrawer } from "@/components/community/filter/community-filter-drawer";
import {
  useGraphQL,
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import type { CommunityVisibilitySettings } from "@/components/community/welcome/community-join-form";
import { CommunityWelcomeStep } from "@/components/community/welcome/community-welcome-step";
import { CommunitySettingsStep } from "@/components/community/welcome/community-settings-step";

const CommunityPageQuery = graphql(`
  query CommunityPageQuery {
    ...CommunityFilterDrawerQueryFragment
  }
`);

const CommunityCurrentUserProfileQuery = graphql(/* GraphQL */ `
  query CommunityCurrentUserProfileQuery($codUsuario: Int!) {
    usuariosProfissionais(where: { codUsuario: { _eq: $codUsuario } }) {
      comunidadeAtiva
      nuncaAcessouComunidade
    }
    ...CommunitySettingsStepFragment
  }
`);

const SaveCommunitySettingsMutation = graphql(/* GraphQL */ `
  mutation SaveCommunitySettingsMutation(
    $codUsuario: Int!
    $comunidadeAtiva: Boolean!
    $nuncaAcessouComunidade: Boolean!
    $telefoneComunidadeInformar: Boolean!
    $ramalTroncoComunidadeInformar: Boolean!
    $websiteComunidadeInformar: Boolean!
  ) {
    update_usuariosProfissionais(
      where: { codUsuario: { _eq: $codUsuario } }
      _set: {
        comunidadeAtiva: $comunidadeAtiva
        nuncaAcessouComunidade: $nuncaAcessouComunidade
        telefoneComunidadeInformar: $telefoneComunidadeInformar
        ramalTroncoComunidadeInformar: $ramalTroncoComunidadeInformar
        websiteComunidadeInformar: $websiteComunidadeInformar
      }
    ) {
      affected_rows
    }
  }
`);

const communitySearchParams = z.object({
  memberName: z.string().optional(),
  tab: z.number().optional(),
  location: z.array(z.string()).optional(),
  specialties: z.array(z.string()).optional(),
  action: z.string().optional(),
});

type CommunityStep = "welcome" | "join-form" | "active";

const CommunityPage = (): JSX.Element => {
  const queryResult = useGraphQL(CommunityPageQuery);
  const { data } = queryResult;
  const { user } = useRouteContext({ from: "__root__" });
  const navigate = useNavigate();

  const profileQueryResult = useGraphQL(CommunityCurrentUserProfileQuery, {
    codUsuario: user.codUsuario,
  });
  const profileData = profileQueryResult.data;
  const invalidateProfile = useInvalidateQuery(CommunityCurrentUserProfileQueryDocument);

  const { mutateAsync: saveCommunitySettings } = useGraphQLMutationWithErrorHandler(
    SaveCommunitySettingsMutation,
  );

  const profissional = profileData?.usuariosProfissionais[0];
  const isCommunityActive = profissional?.comunidadeAtiva ?? false;

  const getInitialStep = (): CommunityStep => {
    if (isCommunityActive) return "active";

    return "welcome";
  };

  const [step, setStep] = useState<CommunityStep>(getInitialStep());
  const [previousStep, setPreviousStep] = useState<CommunityStep>("welcome");

  if (profileData && step === "welcome" && isCommunityActive) {
    setStep("active");
  }

  const goToStep = (next: CommunityStep): void => {
    setPreviousStep(step);
    setStep(next);
  };

  const handleJoinClick = (): void => {
    goToStep("join-form");
  };

  const handleSettingsClick = (): void => {
    goToStep("join-form");
  };

  const handleBackFromForm = (): void => {
    setStep(previousStep === "active" ? "active" : "welcome");
  };

  const handleSave = async (visibility: CommunityVisibilitySettings): Promise<void> => {
    await saveCommunitySettings(
      {
        codUsuario: user.codUsuario,
        comunidadeAtiva: true,
        nuncaAcessouComunidade: false,
        telefoneComunidadeInformar: visibility.whatsapp,
        ramalTroncoComunidadeInformar: visibility.ramal,
        websiteComunidadeInformar: visibility.site,
      },
      {
        onSuccess: () => {
          invalidateProfile();
          setStep("active");
        },
      },
    );
  };

  const handleLeave = async (): Promise<void> => {
    await saveCommunitySettings(
      {
        codUsuario: user.codUsuario,
        comunidadeAtiva: false,
        nuncaAcessouComunidade: false,
        telefoneComunidadeInformar: false,
        ramalTroncoComunidadeInformar: false,
        websiteComunidadeInformar: false,
      },
      {
        onSuccess: () => {
          invalidateProfile();
          void navigate({ to: "/settings" });
        },
      },
    );
  };

  if (step === "welcome") {
    return <CommunityWelcomeStep onJoin={handleJoinClick} />;
  }

  if (step === "join-form" && profileData) {
    return (
      <CommunitySettingsStep
        data={profileData}
        isCommunityActive={isCommunityActive}
        onBack={handleBackFromForm}
        onSave={handleSave}
        onLeave={handleLeave}
      />
    );
  }

  // Comunidade ativa (tela existente)
  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Comunidade" align="center" />
        <HeaderButton icon={Settings} align="end" onClick={handleSettingsClick} />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {data ? <CommunityFilterDrawer data={data} /> : null}
          <CommunityList />
        </AsyncDataWrapper>
      </Page>
    </>
  );
};

export const Route = createFileRoute("/settings/community/")({
  component: CommunityPage,
  validateSearch: communitySearchParams,
});
